<template>
  <el-dialog v-model="innerVisible" title="设计师入驻申请" width="860px" align-center class="designer-dialog">
    <div class="hint">请填写以下信息完成设计师入驻申请，标有 * 的为必填项。提交后我们将在 3-5 个工作日内核您的申请，请保持联系畅通</div>
    <el-form ref="formRef" :model="form" :rules="rules" label-position="top" class="form">
      <div class="section">
        <div class="section-title">基本信息</div>
        <div class="grid two">
          <el-form-item label="设计师姓名 *" prop="name">
            <el-input v-model="form.name" placeholder="请输入真实姓名" />
          </el-form-item>
          <el-form-item label="专业领域 *" prop="domain">
            <el-select v-model="form.domain" placeholder="请选择专业领域">
              <el-option v-for="d in professionalOptions" :key="d.id" :label="d.value" :value="d.id" />
            </el-select>
          </el-form-item>
        </div>
        <div class="grid two">
          <el-form-item label="所在地 *" prop="region">
            <el-cascader ref="regionRef" v-model="form.region" :options="regionOptions" style="width: 100%"
              :props="{ value: 'code', label: 'name', children: 'children', emitPath: true }" filterable
              placeholder="请选择省/市/区">
              <template #label="{ node, data }">
                {{ data.name }}
                {{ node }}
              </template>
            </el-cascader>
          </el-form-item>
          <el-form-item label="从业年限 *" prop="years">
            <el-select v-model="form.years" placeholder="请选择从业年限">
              <el-option v-for="d in experienceOptions" :key="d.id" :label="d.value" :value="d.id" />
            </el-select>
          </el-form-item>
        </div>
        <el-form-item label="个人简介 *" prop="bio">
          <el-input v-model="form.bio" type="textarea" :rows="6" maxlength="800" show-word-limit
            placeholder="请简要介绍您的专业背景、擅长风格、代表作品等" />
        </el-form-item>
      </div>

      <div class="section">
        <div class="section-title">代表作品</div>
        <el-upload ref="uploadRef" class="uploader" drag :show-file-list="false" multiple accept="image/*"
          :before-upload="beforeWorkUpload" :http-request="handleWorkUpload" :on-success="onWorkSuccess">
          <div class="upload-card" v-loading="workUploading" element-loading-text="上传中..."
            element-loading-background="rgba(255,255,255,0.6)">
            <div class="upload-icon">
              <el-icon>
                <Upload />
              </el-icon>
            </div>
            <div class="upload-text-main">点击上传或拖放作品图片至此处</div>
            <div class="upload-text-sub">支持常见图片格式（PNG/JPG/JPEG/WebP/GIF/BMP/SVG），支持多选</div>
          </div>
        </el-upload>
        <div class="works-grid">
          <div v-for="(f, idx) in fileList" :key="f.uid || idx" class="works-item">
            <img :src="f.url" class="works-img" />
            <div class="remove-btn" title="删除" @click="removeWork(idx)">
              <el-icon>
                <Close />
              </el-icon>
            </div>
          </div>
        </div>
      </div>
    </el-form>
    <template #footer>
      <div class="footer-actions">
        <el-button @click="onCancel">取消</el-button>
        <!-- <el-button type="primary" plain @click="onSave">保存草稿</el-button> -->
        <el-button type="primary" @click="onSubmit">提交申请</el-button>
      </div>
    </template>
  </el-dialog>
</template>
<script lang="ts" setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { ElDialog, ElButton, ElForm, ElFormItem, ElInput, ElSelect, ElOption, ElUpload, ElMessage, ElIcon, ElCascader } from 'element-plus'
import { uploadfiles } from '@/api/plugin'
import { Upload, Close } from '@element-plus/icons-vue'
import { businessApply, uploadImage, getDesignerApplyDict } from '@/api/match'
//@ts-ignore
import areaData from '@/common/area.json'
const props = defineProps<{ visible: boolean }>()
const emit = defineEmits(['update:visible', 'save', 'submit'])
const innerVisible = computed({ get: () => props.visible, set: v => emit('update:visible', v) })
const formRef = ref()
const form = reactive({ name: '', domain: '', region: [] as Array<string | number>, years: '', bio: '' })
const rules = {
  name: [{ required: true, message: '请输入真实姓名', trigger: 'blur' }],
  domain: [{ required: true, message: '请选择专业领域', trigger: 'change' }],
  region: [{ required: true, message: '请选择所在地', trigger: 'change' }],
  years: [{ required: true, message: '请选择从业年限', trigger: 'change' }],
  bio: [{ required: true, message: '请输入个人简介', trigger: 'blur' }]
}
const uploadRef = ref()
const workUploading = ref(false)
const fileList = ref<Array<{ url: string, serverUrl?: string, name: string, uid?: string | number, raw: File }>>([])
const beforeWorkUpload = (file: File) => {
  const okType = (file.type && file.type.startsWith('image/')) || /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(file.name || '')
  if (!okType) { ElMessage.error('仅支持图片格式'); return false }
  return true
}
const handleWorkUpload = async (option: any) => {
  workUploading.value = true
  try {
    const raw: File = option?.file?.raw || option?.file
    const fd = new FormData()
    fd.append('file', raw, raw?.name || 'file')
    const res = await uploadImage(fd)
    const url = res?.data?.data?.url || res?.data?.data?.[0]?.url || ''
    option?.onSuccess?.({ url }, option?.file)
  } catch (e) {
    option?.onError?.(e)
    ElMessage.error('上传失败')
  } finally {
    workUploading.value = false
  }
}
const onWorkSuccess = (res: any, file: any) => {
  const raw: File = file?.raw || file
  if (!raw) return
  const uid = file?.uid || raw.name
  if (fileList.value.find(f => f.uid === uid || f.name === raw.name)) return
  const serverUrl = res?.url || ''
  const url = file?.url || URL.createObjectURL(raw)
  fileList.value.push({ url, serverUrl, name: raw.name, uid, raw })
}
const onRemove = (_: any, file: any) => {
  const uid = file?.uid || file?.name
  const idx = fileList.value.findIndex(f => f.uid === uid || f.name === file?.name)
  if (idx >= 0) {
    URL.revokeObjectURL(fileList.value[idx].url)
    fileList.value.splice(idx, 1)
  }
}
const onAddClick = () => {
  try {
    const input = uploadRef.value?.$el?.querySelector('input[type=file]')
    input && input.click()
  } catch { }
}
const removeWork = (idx: number) => {
  const it = fileList.value[idx]
  if (!it) return
  try { if (it.url && String(it.url).startsWith('blob:')) URL.revokeObjectURL(it.url) } catch { }
  fileList.value.splice(idx, 1)
}
const regionOptions = ref<any[]>([])
const regionRef = ref()
const loadRegionOptions = () => {
  regionOptions.value = areaData
}
watch(innerVisible, (v) => { if (v) loadRegionOptions() })
const onCancel = () => { emit('update:visible', false) }
const onSave = () => { emit('save', { ...form, works: fileList.value }) }
const onSubmit = () => {
  console.log(fileList.value.map(f => f.serverUrl || ''))
  formRef.value?.validate((valid: boolean) => {
    if (valid) {
      let regionPath: string[] = []
      try { const node = regionRef.value?.getCheckedNodes?.()[0]; regionPath = Array.isArray(node?.pathLabels) ? node.pathLabels : [] } catch { }
      const codes = Array.isArray(form.region) ? form.region : []
      const city_code = String(codes[1] ?? codes[0] ?? '')
      const city_name = String(regionPath[1] ?? regionPath[0] ?? '')
      const majorItem = professionalOptions.value.find((d: any) => d.id === form.domain)
      const yearItem = experienceOptions.value.find((d: any) => d.id === form.years)
      const piece = fileList.value.map(f => f.serverUrl || '').filter(Boolean).join(',')
      const params = {
        user_type: 3,
        customer_name: form.name,
        contact_person: form.name,
        piece,
        city_code,
        city_name,
        address: '',
        major: (majorItem as any)?.value || '',
        major_dict_id: form.domain,
        year_dict_id: form.years,
        year_range: (yearItem as any)?.value || '',
        introduce: form.bio,
        origin_type: 2
      }; (async () => {
        const res = await businessApply(params)
        if (res?.data?.code === 200) {
          ElMessage.success('提交成功')
          window.location.reload()
          // emit('update:visible', false)
        } else {
          ElMessage.error(res?.data?.msg || '提交失败')
        }
      })()
    }
  })
}
onMounted(() => {
  loadRegionOptions()
  getopt()
})
const professionalOptions = ref([])
const experienceOptions = ref([])
const getopt = async () => {
  let po = await getDesignerApplyDict({ parent_id: '46' })
  let eo = await getDesignerApplyDict({ parent_id: '43' })
  professionalOptions.value = po?.data?.data.list || []
  experienceOptions.value = eo?.data?.data.list || []
}
</script>
<style lang="scss" scoped>
.designer-dialog :deep(.el-dialog__body) {
  padding: 0 0 16px 0;
}

.designer-dialog :deep(.el-dialog__header) {
  border-bottom: 1px solid #eef1f5;
}

.hint {
  background: #edf4ff;
  color: #4b6b9f;
  border: 1px solid #dbe7ff;
  padding: 12px 16px;
  margin: 16px;
  border-radius: 8px;
  font-size: 12px;
}

.form {
  padding: 0 16px 8px;
}

.section {
  background: #fff;
  border: 1px solid #eef1f5;
  border-radius: 12px;
  padding: 16px;
  margin: 16px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2328;
  margin-bottom: 12px;
}

.grid {
  display: grid;
  gap: 12px;
}

.grid.two {
  grid-template-columns: 1fr 1fr;
}

.uploader :deep(.el-upload) {
  width: 100%;
}

.upload-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100px;
  border-radius: 10px;
}

.upload-icon :deep(svg) {
  width: 28px;
  height: 28px;
  color: #2a66ff;
}

.upload-text-main {
  font-size: 14px;
  color: #38393b;
}

.upload-text-sub {
  font-size: 12px;
  color: #8a8f99;
}

.footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 8px;
}

:deep(.el-input__wrapper),
:deep(.el-textarea__inner) {
  border-radius: 8px;
}

:deep(.el-input__wrapper.is-focus),
:deep(.el-select .el-input__wrapper.is-focus),
:deep(.el-textarea__inner:focus) {
  border-color: #2a66ff !important;
  box-shadow: 0 0 0 1px rgba(42, 102, 255, 0.2);
}

/* 作品预览网格 */
.works-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  padding-top: 12px;
}

.works-item {
  height: 72px;
  border: 1px dashed #cfd3dc;
  border-radius: 8px;
  background: #fafbfc;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.works-item {
  position: relative;
}

.remove-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 20px;
  height: 20px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.remove-btn :deep(svg) {
  width: 14px;
  height: 14px;
}

.works-plus {
  font-size: 18px;
  color: #8a8f99;
}

.works-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}
</style>
